// App.tsx

import './App.css';
import { HashRouter, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import Loader from './components/Global/Loader';
import PageLoader from './components/Global/PageLoader';
import React, { useEffect, useState } from 'react';
import PermissionGate from './components/Global/PermissionGate';
import { useLoading } from './components/Global/LoadingContext';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import navItems, { hiddenRoutes } from './Utils/NavItems';
import theme from './theme/theme';
import { BACKEND_STATUS_EVENT, BackendStatusDetail } from './components/Global/authService';
import Header from './components/Global/Header';
import LoginPage from './components/Login/LoginPage';
import Breadcrumbs from './components/Global/Breadcrumbs';
import { clearAllowedScreensCache } from './Utils/CommonUtils';

function App() {
    const { loading } = useLoading();
    const [isLoggedIn, setIsLoggedIn] = useState(localStorage.getItem('token') !== null);
    const [isDrawerExpanded, setIsDrawerExpanded] = React.useState(true);
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = React.useState(false);
    const [backendStatus, setBackendStatus] = useState<BackendStatusDetail>({
        isDown: false,
        message: '',
    });
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // useEffect(()=>{if(!loggedIn){setIsLoggedIn(false)}},[loggedIn])

    const handleLogin = () => {
        // Perform login logic here...
        clearAllowedScreensCache();
        setIsLoggedIn(true);
        const loggedInRole = localStorage.getItem('role') || '';
        if (loggedInRole === 'FACULTY' || loggedInRole === 'STUDENT') {
            window.location.hash = '#/';
            return;
        }
        if (!window.location.hash || window.location.hash === '#/login') {
            window.location.hash = '#/';
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('name');
        localStorage.removeItem('role');
        localStorage.removeItem('branch');
        clearAllowedScreensCache();
        setIsLoggedIn(false);
    };

    useEffect(() => {
        const onSessionReplaced = () => {
            clearAllowedScreensCache();
            setIsLoggedIn(false);
        };
        window.addEventListener('nict:session-replaced', onSessionReplaced);
        return () => window.removeEventListener('nict:session-replaced', onSessionReplaced);
    }, []);

    useEffect(() => {
        if (!isMobile) {
            setIsMobileDrawerOpen(false);
        }
    }, [isMobile]);

    useEffect(() => {
        const onBackendStatus = (event: Event) => {
            const customEvent = event as CustomEvent<BackendStatusDetail>;
            if (!customEvent.detail) {
                return;
            }
            setBackendStatus(customEvent.detail);
        };

        window.addEventListener(BACKEND_STATUS_EVENT, onBackendStatus as EventListener);
        return () => window.removeEventListener(BACKEND_STATUS_EVENT, onBackendStatus as EventListener);
    }, []);

    const bannerHeight = backendStatus.isDown ? 52 : 0;
    const headerHeight = isMobile ? 64 : 72;
    const appPadding = isMobile ? '12px' : '24px';

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <HashRouter>
                <div>
                    {loading && !isLoggedIn && <Loader />}
                    <Collapse in={backendStatus.isDown}>
                        <Alert
                            severity='error'
                            sx={{
                                position: 'fixed',
                                top: isLoggedIn ? `${headerHeight}px` : 0,
                                left: 0,
                                right: 0,
                                zIndex: 1400,
                                borderRadius: 0,
                            }}
                        >
                            {backendStatus.message || 'Backend is unavailable. Some actions may fail until service is restored.'}
                        </Alert>
                    </Collapse>
                    {!isLoggedIn ? (
                        <LoginPage onLogin={handleLogin} />
                    ) : (
                        <>
                            <Header
                                onLogout={handleLogout}
                                isDrawerExpanded={isDrawerExpanded}
                                setIsDrawerExpanded={setIsDrawerExpanded}
                                isMobileDrawerOpen={isMobileDrawerOpen}
                                setIsMobileDrawerOpen={setIsMobileDrawerOpen}
                            />
                            <div
                                className='App'
                                style={{
                                    marginLeft: isMobile ? 0 : (isDrawerExpanded ? '260px' : '72px'),
                                    marginTop: `${headerHeight + bannerHeight}px`,
                                    transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    padding: appPadding,
                                    minHeight: `calc(100dvh - ${headerHeight}px)`,
                                    backgroundColor: 'transparent',
                                }}
                            >
                                {loading && <Loader fullScreen={false} />}
                                <Breadcrumbs />
                                <React.Suspense fallback={<PageLoader />}>
                                    <Routes>
                                        {navItems.map((item) => (
                                            item.children && item.element ? (
                                                item.children.map((child) => (
                                                    <Route
                                                        key={child.title}
                                                        path={child.path}
                                                        element={
                                                            <PermissionGate
                                                                                element={child.element && (
                                                                                    child.path === '/enquiry' ? (
                                                                                        <DndProvider backend={HTML5Backend}>
                                                                                            <child.element />
                                                                                        </DndProvider>
                                                                                    ) : (
                                                                                        <child.element />
                                                                                    )
                                                                                )}
                                                                screenKey={child.path}
                                                                userRoles={child.roles}
                                                            />
                                                        }
                                                    />
                                                ))
                                            ) : (
                                                <Route
                                                    key={item.title}
                                                    path={item.path}
                                                    element={
                                                        <PermissionGate
                                                            element={
                                                                item.path === '/enquiry' ? (
                                                                    <DndProvider backend={HTML5Backend}>
                                                                        <item.element />
                                                                    </DndProvider>
                                                                ) : (
                                                                    <item.element />
                                                                )
                                                            }
                                                            screenKey={item.path}
                                                            userRoles={item.roles}
                                                        />
                                                    }
                                                />
                                            )
                                        ))}

                                        {hiddenRoutes.map((route) => (
                                            <Route
                                                key={route.path}
                                                path={route.path}
                                                element={
                                                    <PermissionGate
                                                        element={<route.element />}
                                                        screenKey={route.path}
                                                        userRoles={route.roles}
                                                    />
                                                }
                                            />
                                        ))}

                                        <Route
                                            path="*"
                                            element={<Navigate to="/" replace />}
                                        />
                                    </Routes>
                                </React.Suspense>
                            </div>
                        </>
                    )}
            </div>
        </HashRouter>
        </ThemeProvider>
    );
}

export default App;
